import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Bot, ShieldCheck, Layers, Megaphone, Zap, Gauge, Lock, Boxes, Cloud } from "lucide-react";
import { SiGooglecloud, SiCisco, SiStripe, SiCloudflare, SiMongodb, SiReact, SiPython, SiKubernetes } from "react-icons/si";
import api from "@/lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5, ease: "easeOut" } }),
};

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/e15ee57d-fd69-49a2-a17c-c96c4cdf8289/images/fa0f96ed424fea91c029c1ac15497bbc30f7cce38bfe59289519c46d5b3c3371.png";

export default function Home() {
  const [portfolio, setPortfolio] = useState([]);

  useEffect(() => {
    api.get("/portfolio").then((r) => setPortfolio(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden scanline" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-60"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-[#050505]" />
        <div className="absolute inset-0 topo-grid opacity-40" />

        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 pt-24 pb-32 md:pt-40 md:pb-48">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <motion.div initial="hidden" animate="show" variants={fadeUp} className="md:col-span-8">
              <div className="flex items-center gap-3 mb-8">
                <span className="inline-block w-10 h-[1px] bg-[#ff3333]" />
                <span className="overline">Ridgeline Cyber Services</span>
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[96px] font-black text-white leading-[0.92] tracking-[-0.03em]" data-testid="hero-title">
                Enterprise software,<br/>
                <span className="text-[#ff3333]">built in weeks.</span><br/>
                Engineered to outlast.
              </h1>
              <p className="mt-8 text-lg text-zinc-300 max-w-2xl leading-relaxed" data-testid="hero-subtitle">
                AI-centric, human-reviewed development. RidgecX delivers web platforms, enterprise products and cybersecurity systems in <span className="text-white font-semibold">hours and weeks</span> — not months.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contact" className="btn-primary" data-testid="hero-cta-primary">
                  Start a project <ArrowUpRight size={18} />
                </Link>
                <Link to="/portfolio" className="btn-secondary" data-testid="hero-cta-secondary">
                  See the work
                </Link>
              </div>
            </motion.div>

            <motion.div initial="hidden" animate="show" custom={1} variants={fadeUp} className="md:col-span-4 border-l border-white/10 pl-6 md:pl-10">
              <div className="space-y-8">
                <StatBlock value="20+" label="Enterprise projects · 90 days" />
                <StatBlock value="15+" label="Products developed" />
                <StatBlock value="100%" label="Before-time delivery" />
              </div>
            </motion.div>
          </div>
        </div>

        {/* TICKER */}
        <div className="relative border-y border-white/10 bg-black/70 backdrop-blur-sm overflow-hidden">
          <div className="ticker flex items-center gap-16 whitespace-nowrap py-4 text-sm mono text-zinc-500">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex items-center gap-16">
                <span>[ AI + HUMAN HYBRID DELIVERY ]</span>
                <span className="text-[#ff3333]">●</span>
                <span>[ ENTERPRISE SOFTWARE ]</span>
                <span className="text-[#ff3333]">●</span>
                <span>[ CYBERSECURITY PLATFORMS ]</span>
                <span className="text-[#ff3333]">●</span>
                <span>[ BRAND & DIGITAL MARKETING ]</span>
                <span className="text-[#ff3333]">●</span>
                <span>[ SHIPPED IN WEEKS — NOT QUARTERS ]</span>
                <span className="text-[#ff3333]">●</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="bg-[#050505] py-24 md:py-32" data-testid="home-services">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-4">
              <p className="overline">What we do</p>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.05]">
                Four practices. One delivery engine.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <ServiceCard
              testId="service-card-ai-hybrid"
              className="md:col-span-7"
              icon={<Bot size={22} />}
              tag="01 · SIGNATURE"
              title="AI + Human Hybrid Development"
              desc="Senior engineers set architecture. AI accelerates implementation. Result: production-grade applications in a fraction of the time, without the drift that pure-AI shops deliver."
              tall
            />
            <ServiceCard
              testId="service-card-cyber"
              className="md:col-span-5"
              icon={<ShieldCheck size={22} />}
              tag="02"
              title="Cybersecurity Platforms"
              desc="Threat-intel consoles, SOC tooling, zero-trust stacks. Hardened, audited, standards-ready."
              tall
            />
            <ServiceCard
              testId="service-card-enterprise"
              className="md:col-span-5"
              icon={<Layers size={22} />}
              tag="03"
              title="Web & Enterprise Software"
              desc="Event-driven platforms, ERPs, portals and SaaS — built for scale from day one."
            />
            <ServiceCard
              testId="service-card-marketing"
              className="md:col-span-7"
              icon={<Megaphone size={22} />}
              tag="04"
              title="Brand, Digital Marketing & Establishment"
              desc="Brand systems, performance funnels, SEO and launches. We treat marketing like engineering: measurable, compounding, accountable."
            />
          </div>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="relative bg-[#0a0a0a] border-y border-white/10 py-24 md:py-32" data-testid="home-process">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-4 md:sticky md:top-24">
              <p className="overline">The Ridgeline Method</p>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.05]">
                Climb the ridge.<br/>Ship the peak.
              </h2>
              <p className="mt-5 text-zinc-400 leading-relaxed">
                Every engagement is a contour — each week a line of elevation toward a production-grade summit. No waterfalls. No eternal sprints. Just elevation.
              </p>
            </div>

            <div className="md:col-span-8 space-y-4">
              {[
                { n: "01", t: "Recon (Week 0)", d: "We compress requirements into a decision tree: architecture, compliance, integrations, risk." , icon: <Gauge size={18}/> },
                { n: "02", t: "Base Camp (Week 1)", d: "Scaffolded environments, CI/CD, auth, data layer. The boring-but-critical skeleton — locked down in days." , icon: <Boxes size={18}/>},
                { n: "03", t: "Ascent (Weeks 2–N)", d: "Hybrid delivery: senior engineers design interfaces, AI implements interiors, reviews are brutal." , icon: <Zap size={18}/>},
                { n: "04", t: "Summit (Ship)", d: "Pen-tested, load-tested, observability-ready. Handover or ongoing retainer — your call." , icon: <Lock size={18}/>},
              ].map((s, i) => (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="grid grid-cols-12 gap-4 border-l-2 border-[#ff3333] bg-[#111]/60 p-6"
                >
                  <div className="col-span-2 md:col-span-1 mono text-[#ff3333] font-bold">{s.n}</div>
                  <div className="col-span-10 md:col-span-11">
                    <div className="flex items-center gap-3">
                      <span className="text-[#ff3333]">{s.icon}</span>
                      <h3 className="font-display text-xl font-bold text-white">{s.t}</h3>
                    </div>
                    <p className="mt-2 text-zinc-400 leading-relaxed">{s.d}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STACK */}
      <section className="py-20 border-b border-white/10" data-testid="home-stack">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <p className="overline text-center">Built on battle-tested stacks</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-14 gap-y-6 text-zinc-500">
            {[Cloud, SiGooglecloud, SiCisco, SiCloudflare, SiMongodb, SiReact, SiPython, SiKubernetes, SiStripe].map((Icon, i) => (
              <Icon key={i} className="w-9 h-9 hover:text-white transition-colors" />
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className="py-24 md:py-32" data-testid="home-portfolio">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between gap-8 mb-12">
            <div>
              <p className="overline">Selected work</p>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.05]">
                Shipped. Scaled.<br/>Still standing.
              </h2>
            </div>
            <Link to="/portfolio" className="hidden md:inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white" data-testid="home-portfolio-viewall">
              All case studies <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {portfolio.map((p) => (
              <Link to="/portfolio" key={p.id} className="group card-brutal p-0 overflow-hidden" data-testid={`home-portfolio-card-${p.id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-black">
                  <img src={p.image_url} alt={p.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                </div>
                <div className="p-6">
                  <p className="overline text-[#ff3333]">{p.category}</p>
                  <h3 className="mt-2 font-display text-xl font-bold text-white">{p.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400 line-clamp-2">{p.summary}</p>
                  <div className="mt-4 flex items-center justify-between text-xs mono text-zinc-500">
                    <span>{p.duration}</span>
                    <span className="text-[#ff3333]">{p.outcome.split(",")[0]}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#050505] py-24 md:py-32" data-testid="home-cta">
        <div className="absolute inset-0 topo-grid opacity-30" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 text-center">
          <p className="overline">Start the ascent</p>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] text-white max-w-4xl mx-auto">
            You have a roadmap.<br/>
            <span className="text-[#ff3333]">We have a delivery engine.</span>
          </h2>
          <p className="mt-6 text-zinc-400 max-w-xl mx-auto">
            Tell us what you're building. We'll respond within one business day with a scoped plan.
          </p>
          <div className="mt-10">
            <Link to="/contact" className="btn-primary" data-testid="home-final-cta">
              Start a project <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const StatBlock = ({ value, label }) => (
  <div>
    <div className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">{value}</div>
    <div className="mt-2 text-xs mono text-zinc-500 uppercase tracking-[0.2em]">{label}</div>
  </div>
);

const ServiceCard = ({ icon, tag, title, desc, className = "", tall = false, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className={`card-brutal group relative ${tall ? "md:min-h-[340px]" : ""} ${className}`}
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <div className="p-2 border border-white/10 text-[#ff3333] group-hover:border-[#ff3333] transition-colors">{icon}</div>
      <span className="text-xs mono text-zinc-500">{tag}</span>
    </div>
    <h3 className="mt-8 font-display text-2xl md:text-3xl font-bold text-white tracking-tight leading-tight">{title}</h3>
    <p className="mt-4 text-zinc-400 leading-relaxed">{desc}</p>
    <div className="mt-6 text-[#ff3333] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 text-sm font-semibold">
      Learn more <ArrowUpRight size={16} />
    </div>
  </motion.div>
);
